import FormStyleProvider from "@client/content/Providers/FormStyle/container";
import { INLINE_BUTTONS_STYLE } from "@client/content/controls/Form/lib";
import { getConsentFieldNames } from "@client/content/lib/consent";
import { getEntityRef, getRootContainerProps } from "@client/content/lib/helper";
import { type Consent, type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { Alert } from "@client/controls/Alert/component";
import { setReferringUserId } from "@client/lib/cookie";
import { useMemoDeep } from "@client/lib/hooks";
import { omit, pick } from "@client/lib/object";
import { isEmailValid } from "@client/lib/validator";
import { type GenericMap } from "@client/types";
import appInfo from "@common/appInfo";
import { isCountryCode, validatePhone } from "@common/components/Form/Field/lib";
import { type OtpStep, OtpStepsModal } from "@common/components/Otp/Modal";
import { type ButtonsAlign } from "@common/contexts/FormStyle";
import ButtonsContainer from "@common/controls/Container/Buttons";
import { type DynamicFormMapOptions, mapFormElements } from "@common/controls/Form/DynamicForm/lib";
import { type DynamicForms } from "@common/controls/Form/DynamicForm/types";
import { FormControlled, type FormControlledRenderParams } from "@common/controls/Form/component";
import { getDialCode } from "@common/controls/Inputs/PhoneInput/lib";
import { SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import apiV1, { ValidationError } from "@common/lib/api";
import { mapFormFieldsWithContextToDynamicFields } from "@common/modules/system/lib";
import classnames from "classnames";
import * as React from "react";
import { Button, PopupButton } from "../Button";
import { type SubmittedArgs } from "../actions";
import api from "../api";
import { type OptInFormV2 } from "../types";

export type MapProps = RootContainerProps & {
  moduleApiInfo: ModuleApiInfo;
  canSubmit: boolean;
  consent: Consent | undefined;
  fieldErrors: GenericMap<string | null>;
  error: string | null;
  message: string | null;
  fields: OptInFormV2.FieldInfo[];
  isInline: boolean;
  isInPopup: boolean;
  popupButtonsAlign?: ButtonsAlign;
  otpOptions: string[];
  isRegisterModule: boolean;
};

export interface ActionProps {
  onSubmitted: (args: SubmittedArgs) => void;
}

export type Props = ActionProps & MapProps;

export default (props: Props) => {
  const i18n = useI18n();

  const [email, setEmail] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isEmailTaken, setIsEmailTaken] = React.useState<boolean | null>(null);
  const [isSubmitted, setIsSubmitted] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    if (appInfo.user || !props.isRegisterModule) return; // skip logged in or opt-in form

    // skip empty/invalid emails
    if (!email || !isEmailValid(email)) {
      setIsEmailTaken(null);
      return;
    }

    setIsLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const res = await apiV1.get<{ isTaken: boolean }>(i18n, `/validate/email`, {
          email
        });

        setIsEmailTaken(res.isTaken);
      } finally {
        setIsLoading(false);
      }
    }, 1000);

    return () => timeout && clearTimeout(timeout);
  }, [email, props.isRegisterModule]);

  const mapOptions = React.useMemo(
    (): DynamicFormMapOptions => ({
      errors: props.fieldErrors,
      requiredMode: "star"
    }),
    [props.fieldErrors]
  );

  const getConsent = React.useCallback(
    (values: GenericMap<any>): any => {
      if (!props.consent) return null;
      return (
        <>
          {!!props.consent?.title && <h4>{props.consent?.title}</h4>}

          {mapFormElements(i18n, props.consent.fields, values, mapOptions)}
        </>
      );
    },
    [props.consent, mapOptions]
  );

  const finalFields = React.useMemo(
    (): DynamicForms.Field[] =>
      mapFormFieldsWithContextToDynamicFields(props.fields, (x) => x.id)
        .filter((x) => {
          const fieldName = x.name;
          if (fieldName === "Password" || fieldName === "ConfirmPassword") {
            if (appInfo.user) return false; // logged in - cannot set/change password

            return isEmailTaken !== null && !isEmailTaken; // hide, until we have validated user email
          }
          return true;
        })
        .map((x) => ({
          ...x,
          disabledTooltip: x.name === "Email" && !!appInfo.user ? i18n.infoEmailDisabled : undefined,
          isDisabled:
            x.isDisabled ||
            ((x.name === "Password" || x.name === "ConfirmPassword") && isLoading) ||
            (x.name === "Email" && !!appInfo.user),
          validations:
            x.formControlKey === "Telephone"
              ? (i18n, y) => (!x.isRequired && isCountryCode(y) ? null : validatePhone(i18n, y)) // allow country code only for non-required phone
              : x.validations
        })),
    [props.fields, isEmailTaken, props.isRegisterModule, email, isLoading]
  );

  const defaultValues = useMemoDeep((): GenericMap<any> => {
    let df = props.fields.reduce((res, x) => {
      let value = x.value;
      if (!value && x.formControlKey === "Telephone") {
        value = getDialCode(appInfo.countryCode);
      }
      return { ...res, [x.id]: value };
    }, {});

    if (props.consent?.fields.length) {
      df = props.consent.fields.reduce((res, x) => ({ ...res, [x.name]: x.value }), df);
    }

    return df;
  }, [props.fields, props.consent?.fields]);

  const [formData, setFormData] = React.useState<GenericMap<any>>(defaultValues);
  const [steps, setSteps] = React.useState<OptInFormV2.Api.OtpStep[]>([]);

  const getFormData = React.useCallback(() => {
    if (formData.Password && formData.Password !== formData.ConfirmPassword)
      throw new ValidationError({
        ConfirmPassword: i18n.passwordsDontMatch
      });

    const consentFieldNames = props.consent ? getConsentFieldNames(props.consent.fields) : [];
    const customFieldsNames = props.fields.filter((y) => y.isCustom).map((x) => x.id);
    const consent = pick(formData, consentFieldNames);
    const customFields = pick(formData, customFieldsNames);

    let rest = omit(formData, consentFieldNames);
    rest = omit(rest, customFieldsNames);

    if ("Telephone" in rest && isCountryCode(rest["Telephone"])) {
      rest.Telephone = null; // if it's country code only, unset
    }

    return [rest, consent, customFields];
  }, [props.consent, props.fields, formData]);

  const submit = React.useCallback(
    async (step?: OtpStep, otp?: string) => {
      const [rest, consent, customFields] = getFormData();
      const result = await api.submit(props.moduleApiInfo, {
        formData: {
          fields: rest,
          consent: consent,
          customFields: customFields
        },
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        otpStepId: step?.id,
        otp: otp
      });
      if (result.error) throw new Error(result.error);

      const data = result.response!.data;
      if (data.otp) {
        setSteps(data.otp.steps);
        return !!data.otp.isCodeValid;
      }

      setIsSubmitted(true);
      props.onSubmitted({
        customFieldValues: customFields,
        enroll: data.enroll,
        fieldValues: rest,
        userId: data.userId
      });
      setSteps([]);
      return true;
    },
    [getFormData, props.moduleApiInfo, props.onSubmitted]
  );

  const onSubmit = React.useCallback(async () => {
    await submit();
  }, [submit]);

  const sendCode = React.useCallback(
    async (step: OtpStep) => {
      const [rest] = getFormData();
      const result = await api.sendOtp(props.moduleApiInfo, {
        fields: rest,
        otpStepId: step.id
      });
      if (result.error) throw new Error(result.error);

      return new Date(result.response!.data.expiryTime);
    },
    [getFormData, props.moduleApiInfo]
  );

  const onChange = React.useCallback(
    (name: string, value: any) => {
      setFormData((x) => ({
        ...x, // don't use formData - breaks when changing phone code (!??)
        [name]: value
      }));

      if (name == "ReferringAffiliateUserId") {
        setReferringUserId(value);
      }

      if (name == "Email") {
        setEmail(value);
      }
    },
    [formData]
  );
  const onOtpClose = React.useCallback(() => setSteps([]), []);

  return (
    <div
      className={classnames("wsc_opt_in_form_container", {
        inline: props.isInline
      })}
    >
      <FormStyleProvider {...getEntityRef(props)}>
        <FormControlled
          defaultValues={defaultValues}
          errorMessage={props.error}
          isInline={props.isInline}
          onChange={onChange}
          requiredMode="star"
          submit={onSubmit}
        >
          {({ isSubmitting, submit, values }: FormControlledRenderParams<GenericMap<any>>) => (
            <>
              {mapFormElements(i18n, finalFields, values, mapOptions)}
              {props.consent?.location === "above" && getConsent(values)}
              <ButtonsContainer
                align={props.isInPopup ? props.popupButtonsAlign : undefined}
                style={props.isInline ? INLINE_BUTTONS_STYLE : undefined}
              >
                {props.isInPopup ? (
                  <PopupButton
                    {...getRootContainerProps(props)}
                    onClick={submit}
                    isDisabled={isSubmitting || !props.canSubmit || isLoading}
                    caption={isLoading ? i18n.verifying : undefined}
                  />
                ) : (
                  <Button
                    {...getRootContainerProps(props)}
                    onClick={submit}
                    isDisabled={isSubmitting || !props.canSubmit || isLoading}
                    caption={isLoading ? i18n.verifying : undefined}
                  />
                )}
              </ButtonsContainer>
              {props.consent?.location === "below" && getConsent(values)}
            </>
          )}
        </FormControlled>

        {props.message && isSubmitted && (
          <Alert color={SystemColor.Blue}>
            <span dangerouslySetInnerHTML={{ __html: props.message }} />
          </Alert>
        )}

        <OtpStepsModal
          isVisible={steps.length > 0}
          onClose={onOtpClose}
          sendCode={sendCode}
          steps={steps}
          validateCode={submit}
        />
      </FormStyleProvider>
    </div>
  );
};
